import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import CloudDownloadOutlined from '@material-ui/icons/CloudDownloadOutlined';
import ExpandMoreOutlined from '@material-ui/icons/ExpandMoreOutlined';
import ExpandLessOutlined from '@material-ui/icons/ExpandLessOutlined';
import CloseOutlined from '@material-ui/icons/CloseOutlined';
import PropTypes from 'prop-types';
import ItemDownloadSegundoPlano from './ItemDownloadSegundoPlano';
import {ativo, textoResumo, progressoGeral} from './downloadSegundoPlano.js';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'fixed',
        right: theme.spacing(2),
        bottom: theme.spacing(2),
        zIndex: theme.zIndex.modal - 1,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        [theme.breakpoints.down('xs')]: {
            left: theme.spacing(1),
            right: theme.spacing(1),
            width: 'auto',
            maxWidth: 'none',
        },
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
    },
    icone: {
        marginTop: 2,
    },
    conteudo: {
        flex: 1,
        minWidth: 0,
    },
    titulo: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
    },
    acoes: {
        display: 'flex',
        alignItems: 'center',
    },
    lista: {
        maxHeight: 'min(50vh, 400px)',
        overflowY: 'auto',
    },
}));

// Painel fixo no canto da tela com os downloads em segundo plano (o mais antigo fica em cima).
export default function PainelDownloadsSegundoPlano(props) {
    const {downloads, onBaixar, onTentarNovamente, onCancelar, onRemover, onLimpar} = props;
    const classes = useStyles();
    const [minimizado, setMinimizado] = useState(false);

    const vazio = downloads.length === 0;

    // Sem downloads, o painel some e volta expandido na próxima vez.
    useEffect(() => {
        if (vazio) {
            setMinimizado(false);
        }
    }, [vazio]);

    if (vazio) {
        return null;
    }

    const algumAtivo = downloads.some(d => ativo(d.situacao));
    const progresso = progressoGeral(downloads);

    return (
        <Paper elevation={6} className={classes.root} role='region' aria-label='Downloads em segundo plano'>
            <div className={classes.cabecalho}>
                <CloudDownloadOutlined color='primary' className={classes.icone}/>
                <div className={classes.conteudo}>
                    <Typography variant='subtitle2' className={classes.titulo}>
                        Downloads
                    </Typography>
                    <Typography variant='body2' color='textSecondary' aria-live='polite'>
                        {textoResumo(downloads)}
                    </Typography>
                </div>
                <div className={classes.acoes}>
                    <IconButton
                        size='small'
                        aria-label={minimizado ? 'Expandir' : 'Minimizar'}
                        onClick={() => setMinimizado(valor => !valor)}>
                        {minimizado
                            ? <ExpandLessOutlined fontSize='small'/>
                            : <ExpandMoreOutlined fontSize='small'/>}
                    </IconButton>
                    {!algumAtivo && (
                        <IconButton size='small' aria-label='Fechar' onClick={onLimpar}>
                            <CloseOutlined fontSize='small'/>
                        </IconButton>
                    )}
                </div>
            </div>
            {minimizado && algumAtivo && (
                <LinearProgress
                    {...(progresso == null
                        ? {variant: 'indeterminate'}
                        : {variant: 'determinate', value: progresso})}/>
            )}
            {!minimizado && (
                <>
                    <Divider/>
                    <div className={classes.lista}>
                        {downloads.map((d, indice) => (
                            <React.Fragment key={d.id}>
                                {indice > 0 && <Divider/>}
                                <ItemDownloadSegundoPlano
                                    download={d}
                                    onBaixar={() => onBaixar(d.id)}
                                    onTentarNovamente={() => onTentarNovamente(d.id)}
                                    onCancelar={() => onCancelar(d.id)}
                                    onRemover={() => onRemover(d.id)}/>
                            </React.Fragment>
                        ))}
                    </div>
                </>
            )}
        </Paper>
    );
}

PainelDownloadsSegundoPlano.propTypes = {
    downloads: PropTypes.array.isRequired,
    onBaixar: PropTypes.func.isRequired,
    onTentarNovamente: PropTypes.func.isRequired,
    onCancelar: PropTypes.func.isRequired,
    onRemover: PropTypes.func.isRequired,
    onLimpar: PropTypes.func.isRequired,
};
